import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  ElementRef,
  viewChild
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideBookOpenCheck,
  lucideCalendarClock,
  lucideFilePenLine,
  lucideFileText,
  lucideNotebookPen,
  lucidePackage,
  lucidePanelLeftOpen,
  lucidePanelRightOpen,
  lucideSchool,
  lucideSquarePen,
  lucideUserPen,
  lucideUsers
} from '@ng-icons/lucide';

import { DayjsService } from '@core/services/dayjs.service';

import { isMenuItemNewActive, MenuItemIsNew } from './sidebar-menu.utils';

interface SidebarMenuSubItemView {
  key: string;
  icon: MenuIcon;
  text: string;
  route: string;
  deprecated: boolean;
  showNewBadge: boolean;
}

interface SidebarMenuGroupView {
  key: string;
  text: string;
  deprecated: boolean;
  showNewBadge: boolean;
  subItems: SidebarMenuSubItemView[];
}

function mapVisibleMenuGroups(
  items: MenuItem[],
  dayjsService: DayjsService
): SidebarMenuGroupView[] {
  const groups: SidebarMenuGroupView[] = [];

  for (const item of items) {
    if (item.disabled === true || item.hidden === true) {
      continue;
    }

    const subItems: SidebarMenuSubItemView[] = [];

    for (const subItem of item.subItems) {
      if (subItem.disabled === true || subItem.hidden === true) {
        continue;
      }

      subItems.push({
        key: subItem.key,
        icon: subItem.icon,
        text: subItem.text,
        route: subItem.route,
        deprecated: subItem.deprecated === true,
        showNewBadge: isMenuItemNewActive(subItem.isNew, dayjsService)
      });
    }

    if (subItems.length === 0) {
      continue;
    }

    groups.push({
      key: item.key,
      text: item.text,
      deprecated: item.deprecated === true,
      showNewBadge: isMenuItemNewActive(item.isNew, dayjsService),
      subItems
    });
  }

  return groups;
}

const ICONS = {
  lucidePanelRightOpen,
  lucidePanelLeftOpen,
  lucideNotebookPen,
  lucideSchool,
  lucideBookOpenCheck,
  lucideSquarePen,
  lucidePackage,
  lucideCalendarClock,
  lucideFilePenLine,
  lucideUserPen,
  lucideUsers,
  lucideFileText
} as const;

type MenuIcon = keyof typeof ICONS;

export interface SubMenuItem {
  key: string;
  icon: MenuIcon;
  text: string;
  route: string;
  disabled?: boolean;
  hidden?: boolean;
  isNew?: MenuItemIsNew;
  deprecated?: boolean;
}

export interface MenuItem {
  key: string;
  text: string;
  subItems: SubMenuItem[];
  disabled?: boolean;
  hidden?: boolean;
  isNew?: MenuItemIsNew;
  deprecated?: boolean;
}

export type { MenuItemIsNew };

@Component({
  selector: 'mw-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  styleUrls: ['./sidebar-menu.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterModule, NgIconComponent],
  providers: [provideIcons(ICONS)],
  animations: [
    trigger('tooltipAnimation', [
      state(
        'void',
        style({
          opacity: 0,
          transform: 'translateY(-50%) scale(0.95)'
        })
      ),
      state(
        '*',
        style({
          opacity: 1,
          transform: 'translateY(-50%) scale(1)'
        })
      ),
      transition('void <=> *', [animate('200ms ease-out')])
    ])
  ]
})
export class SidebarMenuComponent {
  private readonly dayjs = inject(DayjsService);

  readonly sidebarRef = viewChild<ElementRef<HTMLDivElement>>('sidebar');

  readonly menuItems = input.required<MenuItem[]>();
  readonly isExpanded = input.required<boolean>();
  readonly toggleExpanded = output<void>();

  readonly visibleMenuItems = computed(() =>
    mapVisibleMenuGroups(this.menuItems(), this.dayjs)
  );

  readonly tooltip = signal({
    visible: false,
    text: '',
    x: 0,
    y: 0,
    deprecated: false
  });

  toggleSidebar() {
    this.toggleExpanded.emit();
    this.hideTooltip();
  }

  showTooltip(text: string, event: MouseEvent, deprecated = false) {
    const sidebar = this.sidebarRef()?.nativeElement;
    if (!sidebar) {
      return;
    }

    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const sidebarRect = sidebar.getBoundingClientRect();

    requestAnimationFrame(() => {
      this.tooltip.set({
        visible: true,
        text,
        x: sidebarRect.right - 8,
        y: rect.top + rect.height / 2,
        deprecated
      });
    });
  }

  hideTooltip() {
    this.tooltip.update((t) => ({ ...t, visible: false }));
  }
}
